import { compareISO, parseISODate, toISODate } from '@backrs_ui/common-ui/src/utils/dateTime.util';
import {
  MAX_PLAN_SPAN_DAYS,
  resolveDraftStartDate,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import { Blackout } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

export type PlanCoverage = Readonly<{ weeks: number; sessions: number }>;

/** Local midnight today — the earliest day a plan can start from. */
const startOfToday = (): Date => {
  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/**
 * Inclusive ISO bounds for a test date: tomorrow through the longest allowed window. Tomorrow,
 * not today, because a test date on the start date leaves no study day.
 */
export const testDateBounds = (): { min: string; max: string } => {
  const start = startOfToday();
  const min = new Date(start);
  const max = new Date(start);

  min.setDate(min.getDate() + 1);
  max.setDate(max.getDate() + MAX_PLAN_SPAN_DAYS - 1);

  return { min: toISODate(min), max: toISODate(max) };
};

/** True when the date sits inside the allowed window (inclusive). */
export const isTestDateInRange = (testDate: string): boolean => {
  const { min, max } = testDateBounds();

  return compareISO(testDate, min) >= 0 && compareISO(testDate, max) <= 0;
};

/** Inclusive ISO bounds for the study start date: today through the day before the test date. */
export const startDateBounds = (testDate: string | null): { min: string; max: string } => {
  const min = toISODate(startOfToday());
  const { max } = testDateBounds();

  // An unusable test date gives nothing to work back from, so the test date's own ceiling stands in.
  if (!testDate || !isTestDateInRange(testDate)) return { min, max };

  const dayBeforeTest = parseISODate(testDate);

  dayBeforeTest.setDate(dayBeforeTest.getDate() - 1);

  return { min, max: toISODate(dayBeforeTest) };
};

/** True when the start date sits inside the window the picker offers for this test date. */
export const isStartDateInRange = (startDate: string, testDate: string | null): boolean => {
  const { min, max } = startDateBounds(testDate);

  return compareISO(startDate, min) >= 0 && compareISO(startDate, max) <= 0;
};

const isBlackedOut = (iso: string, blackouts: readonly Blackout[]): boolean =>
  blackouts.some((b) => compareISO(iso, b.startDate) >= 0 && compareISO(iso, b.endDate) <= 0);

export type CoverageInputs = Readonly<{
  startDate: string | null;
  testDate: string | null;
  studyDays: readonly number[];
  blackouts: readonly Blackout[];
}>;

/**
 * A pre-plan estimate of the schedule: total weeks from the start date to the test date, and the
 * study sessions falling on the selected weekdays outside any blackout. The engine computes the
 * authoritative plan on completion; this only previews it as inputs change. Null when there is
 * nothing to estimate (no test date, no study days, an empty window) and when the test date is
 * out of range — walking to an arbitrary far-future date would block the render thread, and such
 * a date can't be saved anyway.
 */
export const estimatePlanCoverage = ({
  startDate,
  testDate,
  studyDays,
  blackouts,
}: CoverageInputs): PlanCoverage | null => {
  if (!testDate || studyDays.length === 0) return null;
  if (!isTestDateInRange(testDate)) return null;

  const start = parseISODate(resolveDraftStartDate(startDate));
  const end = parseISODate(testDate);

  if (start >= end) return null;

  const studyDaySet = new Set(studyDays);
  const cursor = new Date(start);
  let totalDays = 0;
  let sessions = 0;

  while (cursor <= end) {
    const iso = toISODate(cursor);

    totalDays += 1;
    // Test day is the sitting itself, never a study session.
    if (iso !== testDate && studyDaySet.has(cursor.getDay()) && !isBlackedOut(iso, blackouts)) {
      sessions += 1;
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return { weeks: Math.max(1, Math.ceil(totalDays / 7)), sessions };
};
